"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";
import { motion } from "framer-motion";
import type { Platform } from "@/payload-types";
import { cn } from "@/lib/utils";
import { getImageData } from "@/lib/media-utils";
import { GridSectionCrosshairs } from "@/payload/blocks/grid-sections/GridSectionCrosshairs";

// Real anchor (crawlable href) that still switches platforms in-place on click
const MotionLink = motion.create(Link);

const LINE_CSS_COLOR = "color-mix(in oklab, white 15%, transparent)";

interface PlatformCarouselProps {
  platforms: Platform[];
  activeIndex: number;
  onSelect: (index: number) => void;
}

/** How many items to show on each side of center */
const VISIBLE_EACH_SIDE = 2;

export function PlatformCarousel({
  platforms,
  activeIndex,
  onSelect,
}: PlatformCarouselProps) {
  const n = platforms.length;

  const prevIndex = (activeIndex - 1 + n) % n;
  const nextIndex = (activeIndex + 1) % n;
  const prevPlatform = platforms[prevIndex];
  const nextPlatform = platforms[nextIndex];

  // Build all items with their offset from center (wrapping around)
  const allItems = platforms.map((platform, i) => {
    let offset = ((i - activeIndex) % n + n) % n;
    if (offset > n / 2) offset -= n;
    return { platform, offset, realIndex: i };
  });

  return (
    <section
      className="relative z-40 w-full text-white border-x border-y border-white/15 grid-decorative-verticals"
      style={{ ["--grid-line-color" as string]: LINE_CSS_COLOR }}
    >
      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-3">
        <div className="grid-cell lg:border-t-0 col-span-1 min-h-48 aspect-square lg:col-span-3 lg:aspect-[3/1] 2xl:aspect-[4/1]">
          <div className="decorative-grid-cell relative flex h-full flex-col justify-center p-6 sm:p-8 lg:p-10">
            {/* Title */}
            <h2 className="text-center text-2xl font-subheading md:text-3xl">
              Platforms
            </h2>

            {/* Carousel */}
            <div className="relative mt-8 flex items-center justify-center">
              {/* Prev arrow */}
              <button
                onClick={() => onSelect(prevIndex)}
                aria-label={`Previous platform: ${prevPlatform.name}`}
                className="group absolute left-0 z-20 flex items-center gap-3 px-5 py-2.5 text-sm text-white/70 transition-colors hover:text-white md:left-4"
              >
                <span className="pointer-events-none absolute left-0 top-0 h-3.5 w-3.5 border-l border-t border-white/40 transition-colors group-hover:border-white" />
                <span className="pointer-events-none absolute right-0 top-0 h-3.5 w-3.5 border-r border-t border-white/40 transition-colors group-hover:border-white" />
                <span className="pointer-events-none absolute bottom-0 left-0 h-3.5 w-3.5 border-b border-l border-white/40 transition-colors group-hover:border-white" />
                <span className="pointer-events-none absolute bottom-0 right-0 h-3.5 w-3.5 border-b border-r border-white/40 transition-colors group-hover:border-white" />
                <span className="text-base leading-none">&lsaquo;</span>
                <span className="hidden sm:inline whitespace-nowrap">
                  {prevPlatform.selectorLabel || prevPlatform.name}
                </span>
              </button>

              {/* Items */}
              <div className="relative h-28 w-full md:h-36 overflow-hidden">
                {allItems.map(({ platform, offset, realIndex }) => {
                  const { url, alt } = getImageData(platform.silhouette, platform.name);
                  const absOffset = Math.abs(offset);
                  const isActive = offset === 0;

                  const scale = isActive ? 1.15 : 0.7;
                  const opacity = absOffset > VISIBLE_EACH_SIDE ? 0 : isActive ? 1 : 0.4;
                  const xPx = offset * 300;

                  return (
                    <MotionLink
                      key={platform.id}
                      href={`/platforms/${platform.slug}`}
                      prefetch={false}
                      draggable={false}
                      onClick={(e) => {
                        e.preventDefault();
                        onSelect(realIndex);
                      }}
                      onKeyDown={(e) => {
                        // Anchors don't activate on Space like buttons do
                        if (e.key === " ") {
                          e.preventDefault();
                          onSelect(realIndex);
                        }
                      }}
                      aria-current={isActive ? "page" : undefined}
                      tabIndex={opacity === 0 ? -1 : undefined}
                      initial={false}
                      animate={{
                        x: xPx,
                        scale,
                        opacity,
                      }}
                      transition={{ duration: 0.4, ease: "easeInOut" }}
                      className={cn(
                        "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center",
                        isActive ? "z-20" : "z-10 cursor-pointer"
                      )}
                    >
                      {url && (
                        <div className="relative h-16 w-32 md:h-24 md:w-48">
                          <Image
                            src={url}
                            alt={alt}
                            fill
                            className="object-contain"
                            sizes="(max-width: 768px) 128px, 192px"
                            unoptimized
                          />
                        </div>
                      )}
                      <span
                        className={cn(
                          "mt-2 text-xs transition-colors md:text-sm whitespace-nowrap",
                          isActive ? "text-white" : "text-white/40"
                        )}
                      >
                        {platform.selectorLabel || platform.name}
                      </span>
                    </MotionLink>
                  );
                })}
              </div>

              {/* Next arrow */}
              <button
                onClick={() => onSelect(nextIndex)}
                aria-label={`Next platform: ${nextPlatform.name}`}
                className="group absolute right-0 z-20 flex items-center gap-3 px-5 py-2.5 text-sm text-white/70 transition-colors hover:text-white md:right-4"
              >
                <span className="pointer-events-none absolute left-0 top-0 h-3.5 w-3.5 border-l border-t border-white/40 transition-colors group-hover:border-white" />
                <span className="pointer-events-none absolute right-0 top-0 h-3.5 w-3.5 border-r border-t border-white/40 transition-colors group-hover:border-white" />
                <span className="pointer-events-none absolute bottom-0 left-0 h-3.5 w-3.5 border-b border-l border-white/40 transition-colors group-hover:border-white" />
                <span className="pointer-events-none absolute bottom-0 right-0 h-3.5 w-3.5 border-b border-r border-white/40 transition-colors group-hover:border-white" />
                <span className="hidden sm:inline whitespace-nowrap">
                  {nextPlatform.selectorLabel || nextPlatform.name}
                </span>
                <span className="text-base leading-none">&rsaquo;</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      <GridSectionCrosshairs />
    </section>
  );
}
